import { useEffect } from 'react';
import { useRouter } from 'next/router';
import shallow from 'zustand/shallow';
import { parseWebVTT } from 'lib/util';
import useVideoPlayerStore from '../../store';

export const useFetchSubtitles = playerTalkData => {
  const router = useRouter();
  const { onSubtitle, onAllSubtitles } = useVideoPlayerStore(
    state => ({ ...state }),
    shallow
  );

  useEffect(() => {
    const abortController = new AbortController();
    const { signal } = abortController;
    const metadataUrl = playerTalkData?.resources.hls.metadata;

    if (metadataUrl) {
      fetch(metadataUrl, { signal })
        .then(response => response.json())
        .then(data => {
          const subtitles = data?.subtitles || [];
          const fetchSubtitlePromises = subtitles.map(subtitle =>
            fetch(subtitle.webvtt, { signal })
              .then(response => response.text())
              .then(text => ({
                language: subtitle.code,
                cues: parseWebVTT(text)
              }))
          );

          Promise.all(fetchSubtitlePromises)
            .then(subtitleData => {
              const subtitleMap: Record<string, string[]> = subtitleData.reduce(
                (map, subtitle) => {
                  map[subtitle.language] = subtitle.cues;
                  return map;
                },
                {}
              );
              onAllSubtitles(subtitleMap);

              // Set onSubtitle for the current subtitle based on the router query
              const currentSubtitleLanguage = router.query?.subtitle || 'en';
              const currentSubtitleCues =
                subtitleMap[currentSubtitleLanguage as string] || [];
              onSubtitle(currentSubtitleCues);
            })
            .catch(error => {
              if (error.name !== 'AbortError') {
                console.error('Error fetching subtitles:', error);
              }
            });
        })
        .catch(error => {
          if (error.name !== 'AbortError') {
            console.error('Error fetching subtitles metadata:', error);
          }
        });
    }

    return () => {
      abortController.abort();
    };
  }, [playerTalkData, onSubtitle, onAllSubtitles, router.query?.subtitle]);
};
